import React, { useState } from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { API_BASE_URL } from "../config/api.config";
import { useFormTracking } from "../hooks/useFormTracking";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    inquiry: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [submitMessage, setSubmitMessage] = useState("");

  // Form tracking
  const {
    handleFieldFocus,
    handleFieldBlur,
    handleFieldChange,
    handleSubmit: trackFormSubmit,
    handleError: trackFormError,
  } = useFormTracking("contact_form", "contact-us-form");

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please provide a valid email address";
    }

    // Phone number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (formData.phoneNumber.trim().length < 10) {
      newErrors.phoneNumber = "Phone number must be at least 10 characters";
    }

    // Inquiry validation
    if (!formData.inquiry.trim()) {
      newErrors.inquiry = "Inquiry/Complaint is required";
    } else if (formData.inquiry.trim().length < 10) {
      newErrors.inquiry = "Inquiry must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Track field change
    handleFieldChange(name, value, e.target.type);

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFocus = (e) => {
    handleFieldFocus(e.target.name, e.target.type);
  };

  const handleBlur = (e) => {
    handleFieldBlur(e.target.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setSubmitMessage("");

    if (!validateForm()) {
      // Track validation errors
      Object.keys(errors).forEach((fieldName) => {
        if (errors[fieldName]) {
          trackFormError(fieldName, "validation_error", errors[fieldName]);
        }
      });
      trackFormSubmit(false);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus("success");
        setSubmitMessage(
          data.message ||
            "Thank you for contacting us! We will get back to you soon.",
        );
        // Track successful submit
        trackFormSubmit(true);
        // Reset form
        setFormData({
          name: "",
          email: "",
          phoneNumber: "",
          inquiry: "",
        });
        setErrors({});
      } else {
        setSubmitStatus("error");
        setSubmitMessage(
          data.message || "Failed to submit the form. Please try again.",
        );
        // Track failed submit
        trackFormSubmit(false);
        trackFormError(
          "form",
          "submit_error",
          data.message || "Failed to submit",
        );
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setSubmitStatus("error");
      setSubmitMessage(
        "An error occurred while submitting the form. Please try again later.",
      );
      // Track error
      trackFormSubmit(false);
      trackFormError("form", "network_error", error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 luxury-gradient">
      <div className="w-full max-w-2xl">
        <div className="luxury-card rounded-2xl shadow-xl p-6 bg-white/95 backdrop-blur-sm">
          <div className="text-center mb-4">
            <h1 className="text-3xl font-bold tracking-tight mb-1">
              Contact Us
            </h1>
            <p className="text-gray-600 text-sm">
              We'd love to hear from you. Send us a message and we'll respond as
              soon as possible.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name and Email Fields - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="Enter your full name"
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="your.email@example.com"
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Phone Number Field */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium mb-1.5">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <Input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder="(123) 456-7890"
                className={errors.phoneNumber ? "border-red-500" : ""}
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-xs mt-0.5">
                  {errors.phoneNumber}
                </p>
              )}
            </div>

            {/* Inquiry/Complaint Field */}
            <div>
              <label
                htmlFor="inquiry"
                className="block text-sm font-medium mb-1.5">
                Inquiry/Complaint <span className="text-red-500">*</span>
              </label>
              <Textarea
                id="inquiry"
                name="inquiry"
                value={formData.inquiry}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder="Please describe your inquiry or complaint in detail..."
                rows={4}
                className={errors.inquiry ? "border-red-500" : ""}
              />
              {errors.inquiry && (
                <p className="text-red-500 text-xs mt-0.5">{errors.inquiry}</p>
              )}
              <p className="text-gray-500 text-xs mt-0.5">
                {formData.inquiry.length}/2000 characters
              </p>
            </div>

            {/* Submit Status Messages */}
            {submitStatus === "success" && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800 text-xs">{submitMessage}</p>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-xs">{submitMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full luxury-button h-10 text-sm font-semibold"
              size="lg">
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </form>

          <div className="mt-4 text-center text-xs text-gray-500">
            <p>
              Need immediate assistance? Call us at{" "}
              <a
                href="tel:+17802677551"
                className="text-blue-600 hover:underline">
                +1 (780) 267-7551
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
